import { useCallback, useEffect, useMemo, useState } from 'react';

import { AggregationFamily, BucketAggregator, SortOrder } from 'enums';

import { postGeneralSearch } from './search-api';
import { Params } from 'types';
import { GeneralSearchItem } from './types';

export function useGeneralSearch(searchedValue: string) {
  const [loading, setLoading] = useState(false);

  const [order, setOrder] = useState<SortOrder>(SortOrder.DESC);
  const [orderBy, setOrderBy] = useState<string>('created');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);

  const [items, setItems] = useState<GeneralSearchItem[]>([]);
  const [count, setCount] = useState(0);

  useEffect(() => {
    const params: Params = {
      filters: [{ operation: 'FTX', value: searchedValue }],
      sort: [{ field: orderBy, order }],
      offset: page * pageSize,
      size: pageSize,
      aggregations: [
        {
          family: AggregationFamily.BUCKET,
          aggregator: BucketAggregator.TERMS,
          name: 'archivalEntityType',
          field: 'archivalEntityType',
        },
      ],
    };

    const search = async () => {
      setLoading(true);
      try {
        const { items, count } = await postGeneralSearch(params);
        setLoading(false);

        if (items && count !== undefined) {
          if (page === 0) {
            setItems(items);
          } else {
            setItems(items);
          }

          setCount(count);
          if (count === 0) {
            setPage(0);
          }
        }
      } catch (e) {
        setLoading(false);
        console.log(e);
      }
    };

    if (searchedValue !== '') {
      search();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, orderBy, page, pageSize, searchedValue]);

  const pageCount = useMemo(() => Math.ceil(count / pageSize), [
    count,
    pageSize,
  ]);

  const handlePageChange = useCallback((page: number) => setPage(page - 1), []);

  return useMemo(
    () => ({
      loading,
      order,
      setOrder,
      orderBy,
      setOrderBy,
      page: page + 1,
      setPage: handlePageChange,
      pageSize,
      setPageSize,
      items,
      count,
      pageCount,
    }),
    [
      loading,
      order,
      orderBy,
      page,
      handlePageChange,
      pageSize,
      items,
      count,
      pageCount,
    ]
  );
}
